import { FunctionComponent, JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { CalendarMonth, DoDisturbOn, History, HourglassBottom, Place, Sms } from '@mui/icons-material'
import styles from './requestCell.module.scss'
import moment, { Moment } from 'moment-timezone'
import Desktop from '@/modules/desktopMobile/desktop'
import Mobile from '@/modules/desktopMobile/mobile'
import { ClockIcon } from '@mui/x-date-pickers'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { RegularCancellationRequest, RequestSlot } from '@/types/Slots.ts'

interface RequestCellProps {
    request: RegularCancellationRequest | RequestSlot
}

const requestCell: FunctionComponent<RequestCellProps> = ({ request }: RequestCellProps) => {
    const { t } = useTranslation()

    return (
        <div className={styles.RequestCell}>
            <div className={styles.Line}>
                {getRequestTypeString(request, t)}
                <div className={styles.TextWithSeparator}>
                    <Desktop>
                        <span className={styles.Separator}>-</span>
                    </Desktop>
                    <p>{request.childName}</p>
                    {request.childComplement && (
                        <p>
                            <span className={styles.Separator}>-</span>
                            {request.childComplement}
                        </p>
                    )}
                </div>
            </div>
            <Desktop>
                <div className={styles.Line}>
                    <div className={styles.IconAndText}>
                        <CalendarMonth className={styles.Icon} />
                        <p className={styles.Text}>
                            {format(parse(request.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                locale: getLocalFromLanguage()
                            })}
                        </p>
                    </div>
                    <div className={styles.TextWithSeparator}>
                        <span className={styles.Separator}>-</span>
                        <p>
                            {t('from')} {moment(request.start, 'HH:mm:ss').format('HH:mm')} {t('to').toLowerCase()}{' '}
                            {moment(request.end, 'HH:mm:ss').format('HH:mm')}
                        </p>
                    </div>
                </div>
            </Desktop>
            <Mobile>
                <div className={styles.Line}>
                    <div className={styles.IconAndText}>
                        <CalendarMonth className={styles.Icon} />
                        <p className={styles.Text}>{moment(request.date, 'DD-MM-YYYY').format('DD/MM/YYYY')}</p>
                    </div>
                </div>
                <div className={styles.Line}>
                    <div className={styles.IconAndText}>
                        <ClockIcon className={styles.Icon} />
                        <p className={styles.Text}>
                            {moment(request.start, 'HH:mm:ss').format('HH:mm')} {' - '}
                            {moment(request.end, 'HH:mm:ss').format('HH:mm')}
                        </p>
                    </div>
                </div>
            </Mobile>
            {request.requestDate && (
                <>
                    <Desktop>
                        <div className={styles.Line}>
                            <div className={styles.IconAndText}>
                                <History className={styles.Icon} />
                                <p className={styles.Text}>
                                    {format(
                                        parse(request.requestDate, 'dd-MM-yyyy HH:mm:ss', new Date()),
                                        'EEEE dd MMMM yyyy HH:mm',
                                        {
                                            locale: getLocalFromLanguage()
                                        }
                                    )}
                                </p>
                            </div>
                            <div className={styles.TextWithSeparator}>
                                <span className={styles.Separator}>-</span>
                                {defineTimeDifference(request, t)}
                            </div>
                        </div>
                    </Desktop>
                    <Mobile>
                        <div className={styles.Line}>
                            <div className={styles.IconAndText}>
                                <History className={styles.Icon} />
                                <div>
                                    <p className={styles.Text}>
                                        {moment(request.requestDate, 'DD-MM-YYYY').format('DD/MM/YYYY')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Mobile>
                </>
            )}
            {isRegularCancellation(request) && (
                <>
                    {request.reason && (
                        <div className={styles.Line}>
                            <div className={styles.IconAndText}>
                                <Sms className={styles.Icon} />
                                <p className={styles.Text}>{request.reason}</p>
                            </div>
                        </div>
                    )}
                    {request.placeId && (
                        <div className={styles.Line}>
                            <div className={styles.IconAndText}>
                                <Place className={styles.Icon} />
                                <p className={styles.Text}>{request.placeName + ' ' + request.placeAddress}</p>
                            </div>
                        </div>
                    )}
                </>
            )}
            {isRequestSlot(request) && request.comment && (
                <div className={styles.Line}>
                    <div className={styles.IconAndText}>
                        <Sms className={styles.Icon} />
                        <p className={styles.Text}>{request.comment}</p>
                    </div>
                </div>
            )}
        </div>
    )
}

export default requestCell

function isRequestSlot(item: RequestSlot | RegularCancellationRequest): item is RequestSlot {
    return 'daycareId' in item
}

function isRegularCancellation(item: RequestSlot | RegularCancellationRequest): item is RegularCancellationRequest {
    return 'reason' in item
}

function getRequestTypeString(request: RegularCancellationRequest | RequestSlot, t: TFunction): JSX.Element {
    if (isRequestSlot(request)) {
        return (
            <div className={styles.IconAndText}>
                <HourglassBottom className={styles.Request} />
                <Desktop>
                    <p className={styles.Text}>{t('requestSlot')}</p>
                </Desktop>
            </div>
        )
    } else if (isRegularCancellation(request)) {
        if (request.start === request.regularSlotStart && request.end === request.regularSlotEnd) {
            return (
                <div className={styles.IconAndText}>
                    <DoDisturbOn className={styles.Absence} />
                    <Desktop>
                        <p className={styles.Text}>{t('absenceRequest')}</p>
                    </Desktop>
                </div>
            )
        } else {
            return (
                <div className={styles.IconAndText}>
                    <HourglassBottom className={styles.RegularRequest} />
                    <Desktop>
                        <p className={styles.Text}>{t('regularEditRequest')}</p>
                    </Desktop>
                </div>
            )
        }
    } else {
        return (
            <div className={styles.IconAndText}>
                <p className={styles.Text}>{t('unknown')}</p>
            </div>
        )
    }
}

function defineTimeDifference(request: RegularCancellationRequest | RequestSlot, t: TFunction): JSX.Element {
    const requestDateMoment: Moment = moment(request.requestDate, 'DD-MM-YYYY HH:mm:ss')
    const dateMoment: Moment = moment(
        moment(request.date, 'DD-MM-YYYY').format('DD-MM-YYYY') +
            ' ' +
            moment(request.start, 'HH:mm:ss').format('HH:mm:ss'),
        'DD-MM-YYYY HH:mm:ss'
    )

    const diff: number = dateMoment.diff(requestDateMoment, 'minutes')
    const days: number = Math.floor(diff / 1440)
    const hoursRemaining: number = Math.floor((diff % 1440) / 60)
    const minutesRemaining: number = Math.floor((diff % 1440) % 60)

    return (
        <p>
            {days > 0 && (
                <>
                    {days} {days > 1 ? t('days') : t('day')}
                </>
            )}
            {hoursRemaining > 0 ? (
                <>
                    {days > 0 && <> {t('and').toLowerCase()} </>}
                    {hoursRemaining} {hoursRemaining > 1 ? t('hours') : t('hour')}
                </>
            ) : (
                <>
                    {minutesRemaining} {minutesRemaining > 1 ? t('minutes') : t('minute')}
                </>
            )}{' '}
            {t('before').toLowerCase()}
        </p>
    )
}
